import z from 'zod';

const zStringWithHints = <T extends string>(..._hints: T[]) =>
  z.string() as unknown as z.ZodType<T | (string & {})>;

export const RequestUsageData = z.object({
  inputTokens: z.number(),
  outputTokens: z.number(),
  totalTokens: z.number(),
  inputTokensDetails: z.record(z.string(), z.number()).optional(),
  outputTokensDetails: z.record(z.string(), z.number()).optional(),
  endpoint: z.string().optional(),
});
export type RequestUsageData = z.infer<typeof RequestUsageData>;

export const UsageData = z.object({
  requests: z.number().optional(),
  inputTokens: z.number(),
  outputTokens: z.number(),
  totalTokens: z.number(),
  inputTokensDetails: z
    .union([
      z.record(z.string(), z.number()),
      z.array(z.record(z.string(), z.number())),
    ])
    .optional(),
  outputTokensDetails: z
    .union([
      z.record(z.string(), z.number()),
      z.array(z.record(z.string(), z.number())),
    ])
    .optional(),
  requestUsageEntries: z.array(RequestUsageData).optional(),
});
export type UsageData = z.infer<typeof UsageData>;

/**
 * 协议中的每个数据项都提供一个 `providerData` 字段，用于容纳自定义功能
 * 或新增字段。
 */
export const SharedBase = z.object({
  /**
   * 额外的、可选的、特定于提供方的数据。用于自定义功能或模型提供方特有的字段。
   */
  providerData: z.record(z.string(), z.any()).optional(),
});
export type SharedBase = z.infer<typeof SharedBase>;

/**
 * 每个数据项都包含一份共享的基础数据，其中包括一个可选的 ID。
 */
export const ItemBase = SharedBase.extend({
  /**
   * 用于标识该数据项的 ID。默认情况下为可选字段。如果模型提供方确实需要此字段，
   * 则会在模型层面进行校验。
   */
  id: z.string().optional(),
});
export type ItemBase = z.infer<typeof ItemBase>;

export const FunctionCallItem = ItemBase.extend({
  type: z.literal('function_call'),
  /**
   * 工具调用的 ID。用于匹配对应的工具调用结果。
   */
  callId: z.string().describe('工具调用的 ID'),

  /**
   * 函数名称。
   */
  name: z.string().describe('函数名称'),

  /**
   * 可选的命名空间，用于限定函数名以便进行工具搜索。
   */
  namespace: z.string().optional(),

  /**
   * 函数调用的状态。
   */
  status: z.enum(['in_progress', 'completed', 'incomplete']).optional(),

  /**
   * 函数调用的参数。
   */
  arguments: z.string(),
});
export type FunctionCallItem = z.infer<typeof FunctionCallItem>;

export const ToolOutputText = SharedBase.extend({
  type: z.literal('text'),

  /**
   * The text output from the model.
   */
  text: z.string(),
});
export type ToolOutputText = z.infer<typeof ToolOutputText>;

/**
 * 这是一个用于兜底的条目类型，用于容纳所有不属于当前协议的内容。
 *
 * 例如，模型可能会使用此类型返回一个不在协议范围内的条目。
 *
 * 在这种情况下，模型返回的所有数据都应通过 `providerData` 字段传递。
 *
 * 这使得模型提供商可以在不破坏协议的情况下添加新功能。
 */
export const UnknownItem = ItemBase.extend({
  type: z.literal('unknown'),
});
export type UnknownItem = z.infer<typeof UnknownItem>;

export const HostedToolCallItem = ItemBase.extend({
  type: z.literal('hosted_tool_call'),
  /**
   * The name of the hosted tool. For example `web_search_call` or `file_search_call`
   */
  name: z.string().describe('The name of the hosted tool'),

  /**
   * The arguments of the hosted tool call.
   */
  arguments: z
    .string()
    .describe('The arguments of the hosted tool call')
    .optional(),

  /**
   * The status of the tool call.
   */
  status: z.string().optional(),

  /**
   * The primary output of the tool call. Additional output might be in the `providerData` field.
   */
  output: z.string().optional(),
});

export type HostedToolCallItem = z.infer<typeof HostedToolCallItem>;

export const ToolCallItem = z.discriminatedUnion('type', [
  FunctionCallItem,
  HostedToolCallItem,
]);
export type ToolCallItem = z.infer<typeof ToolCallItem>;

const MessageBase = ItemBase.extend({
  /**
   * Any item without a type is treated as a message
   */
  type: z.literal('message').optional(),
});

export const InputText = SharedBase.extend({
  type: z.literal('input_text'),
  /**
   * A text input for example a message from a user
   */
  text: z.string(),
});
export type InputText = z.infer<typeof InputText>;

export const InputImage = SharedBase.extend({
  type: z.literal('input_image'),

  /**
   * The image input to the model. Could be provided inline (`image`), as a URL, or by reference to a
   * previously uploaded OpenAI file.
   */
  image: z
    // 1. image data
    .string()
    // 2.file ID for the image
    .or(z.object({ id: z.string().describe('OpenAI file ID') }))
    .describe(
      'Either base64 encoded image data, a data URL, or an object with a file ID.',
    )
    .optional(),

  /**
   * Controls the level of detail requested for image understanding tasks.
   * Future models may add new values, therefore this accepts any string.
   */
  detail: z.string().optional(),
});
export type InputImage = z.infer<typeof InputImage>;

export const InputFile = SharedBase.extend({
  type: z.literal('input_file'),

  /**
   * The file input to the model. Could be raw data, a URL, or an OpenAI file ID.
   * When passing a string, it is interpreted as inline data or a URL; use `{ id }` for file IDs.
   */
  file: z
    // 1. file data
    .string()
    .describe(
      'Either base64 encoded file data or a publicly accessible file URL',
    )
    // 2. file ID
    .or(z.object({ id: z.string().describe('OpenAI file ID') }))
    // 3. publicly accessible file URL
    .or(z.object({ url: z.string().describe('Publicly accessible file URL') }))
    .describe('Contents of the file or an object with a file ID.')
    .optional(),

  /**
   * Optional filename metadata when uploading file data inline.
   */
  filename: z.string().optional(),
});
export type InputFile = z.infer<typeof InputFile>;

export const UserContent = z.discriminatedUnion('type', [
  InputText,
  InputImage,
  InputFile,
]);
export type UserContent = z.infer<typeof UserContent>;

export const UserMessageItem = MessageBase.extend({
  // type: z.literal('message'),

  /**
   * Representing a message from the user
   */
  role: z.literal('user'),

  /**
   * The content of the message.
   */
  content: z.array(UserContent).or(z.string()),
});
export type UserMessageItem = z.infer<typeof UserMessageItem>;

export const ReasoningText = SharedBase.extend({
  type: z.literal('reasoning_text'),
  /**
   * A text input for example a message from a user
   */
  text: z.string(),
});
export type ReasoningText = z.infer<typeof ReasoningText>;

export const ReasoningItem = SharedBase.extend({
  id: z.string().optional(),
  type: z.literal('reasoning'),

  /**
   * The user facing representation of the reasoning. Additional information might be in the `providerData` field.
   */
  content: z.array(InputText),

  /**
   * The raw reasoning text from the model.
   */
  rawContent: z.array(ReasoningText).optional(),
});
export type ReasoningItem = z.infer<typeof ReasoningItem>;

export const OutputText = SharedBase.extend({
  type: z.literal('output_text'),
  text: z.string(),
});

export const Refusal = SharedBase.extend({
  type: z.literal('refusal'),
  refusal: z.string(),
});
export type Refusal = z.infer<typeof Refusal>;

export type OutputText = z.infer<typeof OutputText>;

export const ImageContent = SharedBase.extend({
  type: z.literal('image'),
  image: z.string().describe('Base64 encoded image data'),
});

export type ImageContent = z.infer<typeof ImageContent>;

export const AssistantContent = z.discriminatedUnion('type', [
  OutputText,
  Refusal,
  ImageContent,
]);
export type AssistantContent = z.infer<typeof AssistantContent>;

export const AssistantMessageItem = MessageBase.extend({
  /**
   * Representing a message from the assistant (i.e. the model)
   */
  role: z.literal('assistant'),

  /**
   * The status of the message.
   */
  status: z.enum(['in_progress', 'completed', 'incomplete']),

  /**
   * The content of the message.
   */
  content: z.array(AssistantContent),
});
export type AssistantMessageItem = z.infer<typeof AssistantMessageItem>;

export const OutputModelItem = z.discriminatedUnion('type', [
  AssistantMessageItem,
  // ToolSearchCallItem,
  // ToolSearchOutputItem,
  // HostedToolCallItem,
  FunctionCallItem,
  // ComputerUseCallItem,
  // ShellCallItem,
  // FunctionCallResultItem,
  // ShellCallResultItem,
  ReasoningItem,
  UnknownItem,
]);
export type OutputModelItem = z.infer<typeof OutputModelItem>;

/**
 * Event returned by the model when new output text is available to stream to the user.
 */
export const StreamEventTextStream = SharedBase.extend({
  type: z.literal('output_text_delta'),
  /**
   * The delta text that was streamed by the modelto the user.
   */
  delta: z.string(),
});

export type StreamEventTextStream = z.infer<typeof StreamEventTextStream>;

/**
 * Event returned by the model when new reasoning text is available.
 */
export const StreamEventReasoningStream = SharedBase.extend({
  type: z.literal('reasoning_delta'),
  /**
   * The delta reasoning text that was streamed by the model.
   */
  delta: z.string(),
});

export type StreamEventReasoningStream = z.infer<
  typeof StreamEventReasoningStream
>;

export const StreamEventOutputPart = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('output_text'),
  }),
  z.object({
    type: z.literal('reasoning'),
  }),
  z.object({
    type: z.literal('function_call'),
    callId: z.string(),
    name: z.string(),
  }),
]);
export type StreamEventOutputPart = z.infer<typeof StreamEventOutputPart>;

/**
 * Event returned by the model when a new output part starts streaming.
 */
export const StreamEventOutputPartStarted = SharedBase.extend({
  type: z.literal('output_part_started'),
  /**
   * Stream-local identifier for the output part.
   */
  partId: z.string(),
  /**
   * The kind of output part that started.
   */
  part: StreamEventOutputPart,
});
export type StreamEventOutputPartStarted = z.infer<
  typeof StreamEventOutputPartStarted
>;

/**
 * Event returned by the model when a function call input JSON string fragment is available.
 */
export const StreamEventToolInputDelta = SharedBase.extend({
  type: z.literal('tool_input_delta'),
  /**
   * The output part identifier this input delta belongs to.
   */
  partId: z.string(),
  /**
   * The raw JSON string delta for the function call arguments.
   */
  delta: z.string(),
});
export type StreamEventToolInputDelta = z.infer<
  typeof StreamEventToolInputDelta
>;

/**
 * Event returned by the model when an output part is done streaming.
 */
export const StreamEventOutputPartDone = SharedBase.extend({
  type: z.literal('output_part_done'),
  /**
   * Stream-local identifier for the output part.
   */
  partId: z.string(),
});
export type StreamEventOutputPartDone = z.infer<
  typeof StreamEventOutputPartDone
>;

/**
 * Event returned by the model when a new response is started.
 */
export const StreamEventResponseStarted = SharedBase.extend({
  type: z.literal('response_started'),
});

export type StreamEventResponseStarted = z.infer<
  typeof StreamEventResponseStarted
>;

/**
 * Event returned by the model when a response is completed.
 */
export const StreamEventResponseCompleted = SharedBase.extend({
  type: z.literal('response_done'),
  /**
   * The response from the model.
   */
  response: SharedBase.extend({
    /**
     * The ID of the response.
     */
    id: z.string(),

    /**
     * The transport request ID for this model call, if provided by the model SDK or transport.
     */
    requestId: z.string().optional(),

    /**
     * The usage data for the response.
     */
    usage: UsageData,

    /**
     * The output from the model.
     */
    output: z.array(OutputModelItem),
  }),
});

export type StreamEventResponseCompleted = z.infer<
  typeof StreamEventResponseCompleted
>;

/**
 * Event returned for every item that gets streamed to the model. Used to expose the raw events
 * from the model.
 */
export const StreamEventGenericItem = SharedBase.extend({
  type: z.literal('model'),
  event: z.any().describe('The event from the model'),
});
export type StreamEventGenericItem = z.infer<typeof StreamEventGenericItem>;

export const StreamEvent = z.discriminatedUnion('type', [
  StreamEventTextStream,
  StreamEventReasoningStream,
  StreamEventOutputPartStarted,
  StreamEventToolInputDelta,
  StreamEventOutputPartDone,
  StreamEventResponseCompleted,
  StreamEventResponseStarted,
  StreamEventGenericItem,
]);
export type StreamEvent = z.infer<typeof StreamEvent>;

const ImageDataObjectSchema = z.object({
  data: z
    .union([z.string(), z.instanceof(Uint8Array)])
    .describe(
      'Base64 image data, or raw bytes that will be base64 encoded automatically.',
    ),
  mediaType: z.string().optional(),
});

const ImageUrlObjectSchema = z.object({
  url: z
    .string()
    .describe('Publicly accessible URL pointing to the image content'),
});

const ImageFileIdObjectSchema = z.object({
  fileId: z
    .string()
    .describe('OpenAI file ID referencing uploaded image content'),
});

const ImageObjectSchema = z
  .union([ImageDataObjectSchema, ImageUrlObjectSchema, ImageFileIdObjectSchema])
  .describe('Inline image data or references to uploaded content.');

export const ToolOutputImage = SharedBase.extend({
  type: z.literal('image'),

  /**
   * Inline image content or a reference to an uploaded file. Accepts a URL/data URL string or an
   * object describing the data/url/fileId source.
   */
  image: z.string().or(ImageObjectSchema).optional(),

  /**
   * Controls the requested level of detail for vision models.
   * Use a string to avoid constraining future model capabilities.
   */
  detail: zStringWithHints('low', 'high', 'auto').optional(),
});
export type ToolOutputImage = z.infer<typeof ToolOutputImage>;

const FileDataObjectSchema = z.object({
  data: z
    .union([z.string(), z.instanceof(Uint8Array)])
    .describe(
      'Base64 encoded file data, or raw bytes that will be encoded automatically.',
    ),
  mediaType: z
    .string()
    .describe('IANA media type describing the file contents'),
  filename: z.string().describe('Filename associated with the inline data'),
});

const FileUrlObjectSchema = z.object({
  url: z.string().describe('Publicly accessible URL for the file content'),
  filename: z.string().optional(),
});

const FileIdObjectSchema = z.object({
  id: z.string().describe('OpenAI file ID referencing uploaded content'),
  filename: z.string().optional(),
});

const FileReferenceSchema = z
  .union([
    z.string().describe('Existing data URL or base64 string'),
    FileDataObjectSchema,
    FileUrlObjectSchema,
    FileIdObjectSchema,
  ])
  .describe(
    'Inline data (with metadata) or references pointing to file contents.',
  );

export const ToolOutputFileContent = SharedBase.extend({
  type: z.literal('file'),

  /**
   * File output reference. Provide either a string (data URL / base64), a data object (requires
   * mediaType + filename), or an object pointing to an uploaded file/URL.
   */
  file: FileReferenceSchema,
});

export type ToolOutputFileContent = z.infer<typeof ToolOutputFileContent>;

export const ToolCallOutputContent = z.discriminatedUnion('type', [
  ToolOutputText,
  ToolOutputImage,
  ToolOutputFileContent,
]);
export type ToolCallOutputContent = z.infer<typeof ToolCallOutputContent>;

export const ToolCallStructuredOutput = z.discriminatedUnion('type', [
  InputText,
  InputImage,
  InputFile,
]);
export type ToolCallStructuredOutput = z.infer<typeof ToolCallStructuredOutput>;

export const FunctionCallResultItem = ItemBase.extend({
  type: z.literal('function_call_result'),
  name: z.string().describe('The name of the tool'),

  /**
   * Optional namespace preserved from the originating function call.
   */
  namespace: z.string().optional(),

  /**
   * The ID of the tool call. Required to match up the respective tool call result.
   */
  callId: z.string().describe('The ID of the tool call'),

  /**
   * The status of the tool call.
   */
  status: z.enum(['in_progress', 'completed', 'incomplete']),

  /**
   * The output of the tool call.
   */
  output: z
    .union([
      z.string(),
      ToolCallOutputContent,
      z.array(ToolCallStructuredOutput),
    ])
    .describe(
      'Output returned by the tool call. Supports plain strings, legacy ToolOutput items, or structured input_* items.',
    ),

  /**
   * UI-only structured payload. Never sent to the model; persisted alongside the item and
   * forwarded to frontends for rich rendering.
   */
  display: z.unknown().optional(),
});
export type FunctionCallResultItem = z.infer<typeof FunctionCallResultItem>;

const SystemMessageItem = MessageBase.extend({
  // type: z.literal('message'),

  /**
   * Representing a system message to the user
   */
  role: z.literal('system'),

  /**
   * The content of the message.
   */
  content: z.string(),
});

export type SystemMessageItem = z.infer<typeof SystemMessageItem>;

export const MessageItem = z.discriminatedUnion('role', [
  SystemMessageItem,
  AssistantMessageItem,
  UserMessageItem,
]);

export type MessageItem = z.infer<typeof MessageItem>;

export const ModelItem = z.union([
  UserMessageItem,
  AssistantMessageItem,
  SystemMessageItem,
  HostedToolCallItem,
  FunctionCallItem,
  FunctionCallResultItem,
  ReasoningItem,
  UnknownItem,
]);
export type ModelItem = z.infer<typeof ModelItem>;
